import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Phone, MapPin, MessageCircle, ExternalLink } from "lucide-react";

import { openWhatsApp } from "@/lib/whatsapp";

export default function Contact() {
  const { t } = useTranslation();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.message) { toast.error(t("contact.err_name_msg", "Please provide your name and message")); return; }
    const num = settings?.whatsapp_number || "+6738123456";
    const msg = `Hello Adelina,\n\nName: ${form.name}\nPhone: ${form.phone}\nEmail: ${form.email}\n\n${form.message}`;
    openWhatsApp(num, msg);
    toast.success(t("contact.thanks", "Thank you — we'll be in touch shortly."));
  };

  const mapsEmbed = settings?.google_maps_embed || "";
  const mapsLink = settings?.google_maps_link || "";

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        <div>
          <div className="overline mb-4">{t("contact.overline", "Get in touch")}</div>
          <h1 className="font-serif text-5xl md:text-6xl text-zinc-950 tracking-tight">{t("contact.title", "Let's talk.")}</h1>
          <p className="mt-6 text-lg text-zinc-900 max-w-md">{t("contact.desc")}</p>

          <div className="mt-10 space-y-4 text-zinc-950">
            <div className="flex items-start gap-3"><MapPin className="text-[#b8860b] mt-1 shrink-0" size={18} /><span>{settings?.address}</span></div>
            <div className="flex items-center gap-3"><Phone className="text-[#b8860b]" size={18} /><span>{settings?.phone}</span></div>
            <div className="flex items-center gap-3"><Mail className="text-[#b8860b]" size={18} /><span>{settings?.email}</span></div>
            <div className="flex items-center gap-3"><MessageCircle className="text-[#25D366]" size={18} /><span>{settings?.whatsapp_number}</span></div>
            {mapsLink && (
              <a href={mapsLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[13px] text-[#b8860b] hover:text-[#8a5f07] font-semibold" data-testid="contact-directions">
                {t("about.get_directions", "Get directions")} <ExternalLink size={12} />
              </a>
            )}
          </div>
        </div>

        <form onSubmit={submit} className="bg-zinc-50 rounded-3xl p-8 border border-zinc-200 space-y-4">
          <div><Label>{t("contact.name", "Name")} *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" required /></div>
          <div><Label>{t("contact.email", "Email")}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" /></div>
          <div><Label>{t("contact.phone", "Phone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" /></div>
          <div><Label>{t("contact.message", "Message")} *</Label><Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" required /></div>
          <button type="submit" className="btn-gold w-full" data-testid="contact-submit">{t("contact.send", "Send Message")}</button>
        </form>
      </div>

      {mapsEmbed && (
        <div className="mt-12 rounded-3xl overflow-hidden border border-zinc-200 shadow-sm" data-testid="contact-maps-embed">
          <iframe
            title="Adelina Office Location"
            src={mapsEmbed}
            width="100%"
            height="420"
            style={{ border: 0, display: "block" }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      )}
    </div>
  );
}
